import { useMemo } from 'react';
import { ChipButton } from 'shared/ui/MultiSelect/Components/ChipButton';
import { MenuBaseComponent } from 'shared/ui/MultiSelect/Components/MenuBase';
import { DropdownBase } from 'shared/ui/MultiSelect/DropdownBase';
import type { MultiSelectProps, Option } from 'shared/ui/MultiSelect/interfaces';
import { getMenuBaseProps } from 'shared/ui/MultiSelect/Multiselect/utils';

import style from './Multiselect.module.scss';

/* necessary space between Chip with picked option and hidden options counter  */
const GUTTER_WIDTH = 85;

export function MultiSelectComponent(props: MultiSelectProps) {
  const {
    title = 'Label',
    options = [],
    selectedOptions,
    setSelected,
    name,
    error,
    disabled,
    leftIcon,
    size = 'big',
    renderCustomMenu,
    renderCustomButton,
    menuClassName,
    wrapperClassName,
    withResize,
    isWhite,
    isHighlighted,
  } = props;

  const memoizedOptions = useMemo(() => options, [options]);

  const handleSingleSelect = (option: Option) => {
    const { selected, filteredSelectedOptions } = getMenuBaseProps({ selectedOptions, option });

    if (selected) {
      setSelected(filteredSelectedOptions);
    } else {
      setSelected([...selectedOptions, option]);
    }
  };

  return (
    <div className={wrapperClassName}>
      <DropdownBase
        name={name}
        portalDataTestId="Test-id-multiSelect-menu"
        wrapperClassName="w-full"
        disabled={disabled}
        renderButton={({ onClick, isOpen }) => (
          <>
            {renderCustomButton ? (
              renderCustomButton({
                onClick,
                title,
                selectedOptions,
                setSelected,
                gutterWidth: GUTTER_WIDTH,
                size,
                isOpen,
                disabled,
                error,
              })
            ) : (
              <ChipButton
                onClick={onClick}
                title={title}
                selectedOptions={selectedOptions}
                setSelected={setSelected}
                gutterWidth={GUTTER_WIDTH}
                leftIcon={leftIcon}
                size={size}
                isOpen={isOpen}
                disabled={disabled}
                error={error}
                isWhite={isWhite}
                isHighlighted={isHighlighted}
              />
            )}
          </>
        )}
        renderMenu={({ isOpen, handleClose, menuDefaultWidth }) => (
          <>
            {renderCustomMenu ? (
              renderCustomMenu({
                open: isOpen,
                options: memoizedOptions,
                selectedOptions,
                handleClose,
                handleSelect: setSelected,
                handleSingleSelect,
                menuDefaultWidth,
                withResize,
              })
            ) : (
              <MenuBaseComponent
                open={isOpen}
                options={memoizedOptions}
                selectedOptions={selectedOptions}
                handleSelect={setSelected}
                handleSingleSelect={handleSingleSelect}
                className={menuClassName}
                withResize={withResize}
                menuDefaultWidth={menuDefaultWidth}
              />
            )}
          </>
        )}
      />
      {error && <span className={style.errorText}>{error}</span>}
    </div>
  );
}
